// React hook wrapping the PictoGenerator for easy use in components

import { useMemo } from 'react';
import { PictoGenerator, IPictoGeneratorOptions, IApiCoordinateMarker } from '../core';
import { Matrix } from '../types';
import { useBackgroundImage } from './useBackgroundImage';
import { IBackgroundImage } from '../components';

export interface IUsePictoGeneratorOptions {
  floorPlanImage?: string;
  scalePixelsPerMeter?: number;
  mapIconScale?: number;
  matrix?: Matrix;
  apiMarkers?: IApiCoordinateMarker[];
  dimensions?: { width: number; height: number };
}

export interface IUsePictoGeneratorReturn {
  generator: PictoGenerator | null;
  backgroundImage: IBackgroundImage | undefined;
  isReady: boolean;
}

/**
 * React hook that creates a PictoGenerator instance with image loading
 *
 * @param options Configuration options
 * @returns Generator instance, background image, and ready state
 */
export const usePictoGenerator = (options: IUsePictoGeneratorOptions): IUsePictoGeneratorReturn => {
  const { backgroundImage } = useBackgroundImage(options.floorPlanImage, options.dimensions);

  // Create generator when image is loaded
  const generator = useMemo(() => {
    if (!backgroundImage) {
      return null;
    }

    const generatorOptions: IPictoGeneratorOptions = {
      scaleParams: {
        naturalWidth: backgroundImage.naturalWidth,
        scalePixelsPerMeter: options?.scalePixelsPerMeter ?? 1,
      },
      mapIconScale: options.mapIconScale,
      matrix: options.matrix,
      apiMarkers: options.apiMarkers,
    };

    return new PictoGenerator(generatorOptions);
  }, [backgroundImage, options.scalePixelsPerMeter, options.mapIconScale, options.matrix, options.apiMarkers]);

  const isReady = generator !== null && backgroundImage !== undefined;

  return { generator, backgroundImage, isReady };
};
